/**
 * Author: Harsh Agrawal
 * Summary: This page represents the thank you page. It dynamically displays content based on the subscribed item, presenting a personalized thank you experience.
 */

import React, { useEffect, useState } from "react";
import Header from "../../../components/layout/header";
import Footer from "../../../components/layout/footer";
import Intro from "../../../assets/img/intro.png";
import Intro2x from "../../../assets/img/intro@2x.png";
import trial from "../../../assets/img/trial.png";
import order from "../../../assets/img/order.png";
import upgrade from "../../../assets/img/upgrade.png";
import vveducation from "../../../assets/img/vv-education.png";
import vveducation2 from "../../../assets/img/vv-education-2.png";
import coachingGroup from "../../../assets/img/coaching-group.png";
import { useDispatch } from "react-redux";
import { resetSession } from "../../../reducers/auth";
import { usePreventBackNavigation } from "../../../utils/globalUtils";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import qr from "./../../../assets/img/mobile-qr.svg";
import localStore from "../../../utils/localstore";
import { retrieveHostedPage } from "../../../reducers/chargebee";
import {
  vvLogin,
  vvTrialOffer,
  vvUniversityLogin,
} from "../../../settings.js/dev";
import { TRANSLATION_TYPE } from "../../../utils/constants";
import { getTranslation } from "../../../reducers/translation";
import { getInterpolatedString } from "../../../utils/text";
import TagManager from "react-gtm-module";
import { useLocation } from "react-router-dom";
import launchpadThankYouImg from "../../../assets/img/ML-trial.png";
import { vvMLTrialOffer } from "../../../settings.js/dev";

function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { estimate, flexTrack, ecommerceGtmEvent } = useSelector(
    (state) => state.cart
  );
  const { hostedPageId } = useSelector((state) => state.chargebee);
  const t = useSelector(getTranslation);
  const t_label = t[TRANSLATION_TYPE.LABEL] ?? {};
  const t_message = t[TRANSLATION_TYPE.MESSAGE] ?? {};

  const location = useLocation();

  usePreventBackNavigation();

  useEffect(() => {
    var thankyouPageType = localStore.get("thankyouPageType");

    trackFlexTrack();
    sendGTMEvent(ecommerceGtmEvent);

    setTimeout(() => {
      dispatch(resetSession());
    }, 1000);
  }, []);

  const sendGTMEvent = (gtm_event) => {
    if (gtm_event != null) {
      TagManager.dataLayer({
        dataLayer: {
          event: "cb_ecommerce",
          pagePath: location.pathname,
          pageTitle: document.title,
          ecommerce_order: gtm_event,
        },
      });
    }
  };

  const trackFlexTrack = async () => {
    let flexTrack_ = flexTrack;
    if (hostedPageId != null) {
      flexTrack_ = await dispatch(retrieveHostedPage(hostedPageId));
    }
    if (flexTrack_.orderNumber != null) {
      let { orderNumber, orderAmount, coupon, currencyCode } = flexTrack_;
      window.flextrack.track({
        order_number: orderNumber,
        order_amount: orderAmount,
        order_coupon: coupon,
        order_currency: currencyCode,
      });
    }
  };

  const thankYouImg = () => {
    var thankyouPageType = localStore.get("thankyouPageType");

    if (thankyouPageType === "ML_TRIAL") {
      return (
        <img
          src={launchpadThankYouImg}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    }

    var vvImage = (
      <img
        src={order}
        width="409"
        height="225"
        alt=""
        className="thanks__img"
      />
    );
    if (thankyouPageType === "TRIAL")
      vvImage = (
        <img
          src={trial}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    else if (thankyouPageType === "GROUP_COACHING")
      vvImage = (
        <img
          src={coachingGroup}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    else if (thankyouPageType === "COURSE")
      vvImage = (
        <img
          src={vveducation}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    else if (thankyouPageType === "IN_PERSION")
      vvImage = (
        <img
          src={vveducation2}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    else if (thankyouPageType === "UPGRADE")
      vvImage = (
        <img
          src={upgrade}
          width="409"
          height="225"
          alt=""
          className="thanks__img"
        />
      );
    return vvImage;
  };

  const thankYouTitle = () => {
    var thankyouPageType = localStore.get("thankyouPageType");
    if (thankyouPageType === "ML_TRIAL")
      return (
        t_message?.["checkout.message.welcome_to_vv_trial"] ??
        "Welcome to Your VectorVest Trial!"
      );
    else if (thankyouPageType === "TRIAL")
      return (
        t_message?.["checkout.message.welcome_to_vv_trial"] ??
        "Welcome to Your VectorVest Trial!"
      );
    else if (thankyouPageType === "GROUP_COACHING")
      return (
        t_message?.["checkout.message.welcome_to_coaching_group"] ??
        "Welcome to the Coaching Group!"
      );
    else if (thankyouPageType === "IN_PERSION" || thankyouPageType === "COURSE")
      return (
        t_message?.["checkout.message.thankyou_vv_education"] ??
        "Thank You for Choosing VectorVest Education!"
      );
    else if (thankyouPageType === "UPGRADE")
      return (
        t_message?.["checkout.message.thankyou_upgrade"] ??
        "Thank You for Your Upgrade!"
      );
    else
      return (
        t_message?.["checkout.message.thankyou_order"] ??
        "Thank You for Your Order!"
      );
  };

  const thankYouSubtitle = () => {
    var thankyouPageType = localStore.get("thankyouPageType");

    var checkoutmessage_thankyousubtitle = getInterpolatedString(
      t_message?.["checkout.message.thankyou_subtitle"],
      { vvLogin }
    );
    var emailconfirmation_text = '';

    switch (thankyouPageType) {
      case "ML_TRIAL":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation"] ?? "An email confirming your order will arrive shortly. If you have any questions, please call us at 1-888-658-7638";
        checkoutmessage_thankyousubtitle = '';
        break;
      case "TRIAL":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation"] ?? "An email confirming your order will arrive shortly. If you have any questions, please call us at 1-888-658-7638";
        break;
      case "GROUP_COACHING":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation_coaching_group"] ?? "An email confirming your order will arrive shortly. Access all your Coaching Group content through your VectorVest University Dashboard.";
        break;
      case "COURSE":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation_course"] ?? "An email confirming your order will arrive shortly. Access your new course in the 'My Courses' section of your VectorVest University Dashboard.";
        break;
      case "IN-PERSION":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation_in_person_course"] ?? "We look forward to seeing you in person! An email confirming your order will arrive shortly with all of the information you need for the event.";
        break;
      case "UPGRADE":
        emailconfirmation_text = t_message?.["checkout.message.email_confirmation_reopen_window"] ?? "Shortly, you'll receive an email confirming your order along with instructions. If you're currently logged into VectorVest 7, kindly close and reopen the program to ensure proper updates.";
        break;

      default:
        emailconfirmation_text = t_message?.["checkout.message.email_order_instruction"] ?? "Shortly, you'll receive an email confirming your order along with instructions.";
    }

    return emailconfirmation_text + " " + checkoutmessage_thankyousubtitle;

  };

  const thankYouPageContent = () => {
    var thankyouPageType = localStore.get("thankyouPageType");

    if (thankyouPageType === "ML_TRIAL") {
      return (
        <>
          <div>
            {t_message?.["checkout.message.launchpadtext"] ??
              "Click below to access tips, resources, and next steps to make the most of your trial."}
          </div>
          <a
            href={vvMLTrialOffer}
            className="button thanks__button"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t_label?.["checkout.label.get_started_with_trial"] ??
              "Get Started with Your Trial"}
          </a>
        </>
      );
    }

    return (
      <>
        {thankyouPageType === "TRIAL" && (
          <>
            <div>
              <a href={vvTrialOffer} className="button thanks__button">
                {t_label?.["checkout.label.get_started_now"] ??
                  "Get Started Now →"}
              </a>
            </div>
          </>
        )}
        {(thankyouPageType === "GROUP_COACHING" ||
          thankyouPageType === "COURSE") && (
            <>
              <div>
                <a href={vvUniversityLogin} className="button thanks__button">
                  {t_label?.["checkout.label.access_dashboard"] ??
                    "Access Dashboard"}
                </a>
              </div>
            </>
          )}
      </>
    );
  };

  const appleStore = () => {
    return (
      <a
        href="https://apps.apple.com/us/app/vectorvest-stock-advisory/id1174967174"
        className="markets-nav__item markets-nav__item--link"
      >
        <svg
          width="18"
          height="24"
          viewBox="0 0 384 512"
          fill="black"
          className="markets-nav__logo"
        >
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"></path>
        </svg>
        <div>
          <div>
            {t_message?.["checkout.message.download_on_the"] ??
              "Download on the"}
          </div>
          <svg
            width="75"
            height="19"
            viewBox="0 0 75 19"
            fill="black"
            className="markets-nav__caption"
          >
            <path d="M7.86875 11.6514H3.1391L2.00329 15.0052H0L4.47984 2.59706H6.56119L11.041 15.0052H9.00359L7.86875 11.6514ZM3.62896 10.1038H7.37798L5.52983 4.66087H5.47807L3.62896 10.1038Z"></path>
            <path d="M20.715 10.4822C20.715 13.2935 19.2104 15.0997 16.9397 15.0997C16.3645 15.1297 15.7925 14.9972 15.2891 14.7173C14.7857 14.4374 14.3713 14.0214 14.0933 13.5169H14.0504V17.9977H12.1934V5.95848H13.9908V7.46314H14.025C14.3157 6.96108 14.7371 6.54722 15.2443 6.26559C15.7516 5.98397 16.3257 5.84512 16.9055 5.86382C19.2016 5.86383 20.715 7.67879 20.715 10.4822ZM18.8064 10.4822C18.8064 8.65069 17.8599 7.44656 16.4157 7.44656C14.9969 7.44656 14.0426 8.67605 14.0426 10.4822C14.0426 12.305 14.9969 13.5257 16.4157 13.5257C17.8599 13.5257 18.8064 12.3304 18.8064 10.4822Z"></path>
            <path d="M30.6724 10.4822C30.6724 13.2935 29.1673 15.0997 26.8966 15.0997C26.3214 15.1297 25.7494 14.9972 25.246 14.7173C24.7426 14.4374 24.3282 14.0214 24.0503 13.5169H24.0073V17.9977H22.1504V5.95848H23.9478V7.46314H23.9819C24.2727 6.96108 24.694 6.54722 25.2013 6.26559C25.7085 5.98397 26.2826 5.84512 26.8624 5.86382C29.1585 5.86383 30.6724 7.67879 30.6724 10.4822ZM28.7633 10.4822C28.7633 8.65069 27.8168 7.44656 26.3726 7.44656C24.9538 7.44656 23.9995 8.67605 23.9995 10.4822C23.9995 12.305 24.9538 13.5257 26.3726 13.5257C27.8168 13.5257 28.7633 12.3304 28.7633 10.4822Z"></path>
            <path d="M37.2517 11.5478C37.3893 12.7783 38.5847 13.5862 40.2181 13.5862C41.7833 13.5862 42.9093 12.7782 42.9093 11.6688C42.9093 10.7057 42.2302 10.129 40.6221 9.73379L39.014 9.34639C36.7355 8.79605 35.6778 7.73049 35.6778 6.00139C35.6778 3.86051 37.5435 2.39 40.1918 2.39C42.8147 2.39 44.6121 3.86051 44.6726 6.00139H42.7981C42.6859 4.76311 41.6623 4.01567 40.1664 4.01567C38.6705 4.01567 37.6469 4.77191 37.6469 5.87259C37.6469 6.74983 38.3007 7.26602 39.9 7.66117L41.2671 7.99685C43.8129 8.59891 44.8697 9.62156 44.8697 11.4365C44.8697 13.7579 43.0215 15.2118 40.0805 15.2118C37.3287 15.2118 35.4709 13.792 35.3508 11.5477L37.2517 11.5478Z"></path>
            <path d="M48.8791 3.81764V5.95852H50.5994V7.42903H48.8791V12.4163C48.8791 13.1911 49.2236 13.5521 49.9798 13.5521C50.1841 13.5485 50.3879 13.5342 50.5907 13.5091V14.9709C50.2507 15.0344 49.9051 15.0632 49.5593 15.0567C47.7277 15.0567 47.0134 14.3688 47.0134 12.6143V7.42903H45.6981V5.95852H47.0134V3.81764H48.8791Z"></path>
            <path d="M51.5957 10.4823C51.5957 7.6359 53.2721 5.84728 55.8863 5.84728C58.5092 5.84728 60.1778 7.63585 60.1778 10.4823C60.1778 13.3364 58.518 15.1173 55.8863 15.1173C53.2555 15.1173 51.5957 13.3364 51.5957 10.4823ZM58.2857 10.4823C58.2857 8.52971 57.3909 7.37731 55.8863 7.37731C54.3816 7.37731 53.4868 8.5385 53.4868 10.4823C53.4868 12.4426 54.3816 13.5862 55.8863 13.5862C57.3909 13.5862 58.2857 12.4426 58.2857 10.4823Z"></path>
            <path d="M61.7088 5.95852H63.4799V7.49829H63.5228C63.6427 7.01739 63.9244 6.5924 64.3207 6.29475C64.7169 5.9971 65.2036 5.84494 65.6988 5.86385C65.9128 5.8631 66.1262 5.88635 66.335 5.93314V7.67004C66.0649 7.58749 65.7831 7.54958 65.5007 7.55783C65.2309 7.54688 64.962 7.59445 64.7124 7.69725C64.4627 7.80006 64.2383 7.95567 64.0545 8.1534C63.8706 8.35113 63.7318 8.58631 63.6474 8.84277C63.563 9.09924 63.5352 9.37092 63.5657 9.63917V15.005H61.7088L61.7088 5.95852Z"></path>
            <path d="M74.8966 12.348C74.6468 13.9902 73.0474 15.1173 71.0012 15.1173C68.3695 15.1173 66.7361 13.354 66.7361 10.5252C66.7361 7.68761 68.3783 5.84728 70.9232 5.84728C73.426 5.84728 75 7.56661 75 10.3095V10.9458H68.6105V11.058C68.5811 11.3909 68.6227 11.7263 68.7328 12.0419C68.8429 12.3575 69.0189 12.646 69.2491 12.8884C69.4792 13.1308 69.7583 13.3214 70.0678 13.4476C70.3773 13.5738 70.7101 13.6327 71.0442 13.6204C71.483 13.6615 71.9233 13.5599 72.2997 13.3306C72.6762 13.1014 72.9685 12.7567 73.1333 12.348L74.8966 12.348ZM68.6193 9.64797H73.1421C73.1588 9.34861 73.1134 9.04904 73.0088 8.76805C72.9042 8.48706 72.7426 8.23073 72.5343 8.01513C72.3259 7.79954 72.0753 7.62934 71.798 7.51521C71.5208 7.40109 71.2229 7.3455 70.9232 7.35194C70.6208 7.35014 70.321 7.40823 70.0412 7.52289C69.7614 7.63754 69.507 7.80647 69.2928 8.01993C69.0786 8.2334 68.9089 8.48717 68.7933 8.7666C68.6777 9.04602 68.6185 9.34557 68.6193 9.64797Z"></path>
          </svg>
        </div>
      </a>
    );
  };

  const playStore = () => {
    return (
      <a
        href="https://play.google.com/store/apps/details?id=com.vectorvest.mango&hl=en_US&gl=US"
        className="markets-nav__item markets-nav__item--link"
      >
        <svg
          width="22"
          height="24"
          viewBox="0 0 22 24"
          fill="none"
          className="markets-nav__logo"
        >
          <path
            d="M10.2866 11.4618L0.352951 22.0058C0.353325 22.0079 0.354071 22.0098 0.354421 22.012C0.65908 23.157 1.70486 24 2.94597 24C3.44211 24 3.90792 23.8659 4.30739 23.6304L4.33912 23.6118L15.5214 17.1592L10.2866 11.4618Z"
            fill="#EB3131"
          ></path>
          <path
            d="M20.3361 9.66675L20.3266 9.66024L15.4989 6.86153L10.0598 11.7014L15.5178 17.1587L20.32 14.3878C21.1619 13.9333 21.7335 13.0457 21.7335 12.0222C21.7335 11.006 21.1697 10.1228 20.3361 9.66675Z"
            fill="#F6B60B"
          ></path>
          <path
            d="M0.351108 1.99349C0.29139 2.21371 0.260002 2.44437 0.260002 2.6839V21.3161C0.260002 21.5551 0.29102 21.7866 0.351487 22.006L10.6281 11.7315L0.351108 1.99349Z"
            fill="#5778C5"
          ></path>
          <path
            d="M10.3591 12L15.5011 6.85952L4.33148 0.383512C3.92548 0.140341 3.45163 0 2.94458 0C1.70347 0 0.656278 0.844581 0.351562 1.99087C0.351189 1.99199 0.351189 1.99276 0.351189 1.99374L10.3591 12Z"
            fill="#3BAD49"
          ></path>
        </svg>
        <div>
          <div>{t_label?.["checkout.label.get_it_on"] ?? "GET IT ON"}</div>
          <svg
            width="85"
            height="19"
            viewBox="0 0 85 19"
            fill="black"
            className="markets-nav__caption"
          >
            <path d="M65.7828 14.9312H67.6456V2.45022H65.7828V14.9312ZM82.5616 6.94648L80.4262 12.3573H80.3624L78.1461 6.94648H76.1395L79.4639 14.5089L77.5682 18.7157H79.5109L84.6332 6.94686L82.5616 6.94648ZM71.9967 13.5135C71.3877 13.5135 70.5362 13.2081 70.5362 12.4536C70.5362 11.4903 71.5964 11.121 72.5109 11.121C73.3295 11.121 73.7158 11.2973 74.213 11.5383C74.0682 12.6945 73.0729 13.5135 71.9967 13.5135ZM72.2223 6.67349C70.8736 6.67349 69.4769 7.26778 68.8989 8.58438L70.5522 9.27461C70.9056 8.58438 71.5634 8.35978 72.2543 8.35978C73.2176 8.35978 74.197 8.9373 74.2129 9.96526V10.0935C73.8755 9.90086 73.1527 9.61196 72.2702 9.61196C70.4872 9.61196 68.6733 10.5911 68.6733 12.4216C68.6733 14.0914 70.1349 15.1673 71.7721 15.1673C73.024 15.1673 73.7158 14.6054 74.148 13.9468H74.2129V14.9107H76.0119V10.1255C76.0119 7.90967 74.3567 6.67349 72.2223 6.67349ZM60.7095 8.46578H58.06V4.18793H60.7095C62.1022 4.18793 62.8928 5.34071 62.8928 6.32681C62.8928 7.29398 62.1021 8.46578 60.7095 8.46578ZM60.6616 2.45052H56.198V14.9315H58.06V10.2032H60.6617C62.7262 10.2032 64.7558 8.70882 64.7558 6.32726C64.7558 3.94616 62.7261 2.45052 60.6616 2.45052ZM36.3219 13.5156C35.0349 13.5156 33.9579 12.4377 33.9579 10.9586C33.9579 9.46229 35.0349 8.36919 36.3219 8.36919C37.5925 8.36919 38.5896 9.46236 38.5896 10.9586C38.5896 12.4377 37.5925 13.5156 36.3219 13.5156ZM38.4609 7.64514H38.3965C37.9785 7.14668 37.1739 6.69632 36.1612 6.69632C34.0378 6.69632 32.0919 8.56229 32.0919 10.9586C32.0919 13.3386 34.0378 15.1881 36.1612 15.1881C37.1739 15.1881 37.9784 14.7377 38.3965 14.2234H38.4609V14.834C38.4609 16.4591 37.5925 17.3271 36.1932 17.3271C35.0517 17.3271 34.344 16.507 34.0542 15.8155L32.4301 16.4909C32.8961 17.6163 34.1342 19 36.1933 19C38.381 19 40.2305 17.713 40.2305 14.5766V6.95355H38.4609L38.4609 7.64514ZM41.5174 14.9308H43.3823V2.44985H41.5174V14.9308ZM46.1327 10.8136C46.0847 9.17297 47.404 8.33684 48.3524 8.33684C49.0925 8.33684 49.7192 8.70691 49.929 9.23729L46.1327 10.8136ZM51.9225 9.39797C51.5688 8.4496 50.4914 6.69632 48.288 6.69632C46.1003 6.69632 44.2831 8.41718 44.2831 10.9423C44.2831 13.3226 46.0847 15.1882 48.4967 15.1882C50.4426 15.1882 51.5689 13.9985 52.0356 13.3066L50.588 12.3415C50.1053 13.0493 49.4463 13.5157 48.4967 13.5157C47.5483 13.5157 46.8725 13.0813 46.4389 12.2289L52.1155 9.88084L51.9225 9.39797ZM6.69115 7.99915V9.80042H11.0013C10.8727 10.8136 10.5349 11.5533 10.0202 12.0681C9.39273 12.6951 8.41163 13.3869 6.69107 13.3869C4.03727 13.3869 1.9627 11.248 1.9627 8.59426C1.9627 5.94053 4.03727 3.80127 6.69107 3.80127C8.12254 3.80127 9.16772 4.36432 9.93976 5.08808L11.2107 3.81722C10.1328 2.78806 8.70162 2 6.69107 2C3.05573 2 0 4.95937 0 8.59426C0 12.2288 3.05573 15.1882 6.69107 15.1882C8.65295 15.1882 10.1329 14.5448 11.2907 13.3387C12.4809 12.1484 12.8509 10.4759 12.8509 9.12468C12.8509 8.70698 12.8185 8.32097 12.7542 7.99915H6.69115ZM17.7517 13.5156C16.4648 13.5156 15.355 12.4541 15.355 10.9423C15.355 9.41437 16.4648 8.36926 17.7517 8.36926C19.0381 8.36926 20.148 9.41437 20.148 10.9423C20.148 12.4541 19.0381 13.5156 17.7517 13.5156ZM17.7517 6.69632C15.4029 6.69632 13.4893 8.4815 13.4893 10.9423C13.4893 13.3869 15.4029 15.1882 17.7517 15.1882C20.0997 15.1882 22.0136 13.3869 22.0136 10.9423C22.0136 8.4815 20.0997 6.69632 17.7517 6.69632ZM27.049 13.5156C25.7629 13.5156 24.6527 12.4541 24.6527 10.9423C24.6527 9.41437 25.763 8.36926 27.049 8.36926C28.3359 8.36926 29.4454 9.41437 29.4454 10.9423C29.4454 12.4541 28.3359 13.5156 27.049 13.5156ZM27.049 6.69632C24.701 6.69632 22.7875 8.4815 22.7875 10.9423C22.7875 13.3869 24.701 15.1882 27.049 15.1882C29.3978 15.1882 31.3114 13.3869 31.3114 10.9423C31.3114 8.4815 29.3978 6.69632 27.049 6.69632Z"></path>
          </svg>
        </div>
      </a>
    );
  };

  return (
    <>
      <div className="body body--alt">
        <Header />

        <main className="main main--fullh">
          <section className="section thanks">
            <div className="grid-cont">
              {thankYouImg()}
              <h1 className="thanks__title">{thankYouTitle()}</h1>

              <p className="thanks__subtitle">
                <span
                  class=""
                  dangerouslySetInnerHTML={{
                    __html: `${thankYouSubtitle()}`,
                  }}
                />
              </p>

              <div className="mt-7">{thankYouPageContent()}</div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default ThankYou;
